import { useEffect } from "react";
import { COOKIE_CONSENT_EVENT, CookieConsent, getCookieConsent } from "@/lib/cookieConsent";

const GA_SCRIPT_ID = "vmb-ga-script";
const META_PIXEL_SCRIPT_ID = "vmb-meta-pixel-script";

const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
const metaPixelId = import.meta.env.VITE_META_PIXEL_ID?.trim();

const loadGoogleAnalytics = (measurementId: string) => {
  if (document.getElementById(GA_SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  const win = window as Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  win.dataLayer = win.dataLayer || [];
  win.gtag = (...args: unknown[]) => {
    win.dataLayer?.push(args);
  };
  win.gtag("js", new Date());
  win.gtag("config", measurementId, { anonymize_ip: true });
};

const loadMetaPixel = (pixelId: string) => {
  if (document.getElementById(META_PIXEL_SCRIPT_ID)) return;

  const win = window as Window & { fbq?: (...args: unknown[]) => void; _fbq?: (...args: unknown[]) => void };

  if (!win.fbq) {
    const fbq = function (...args: unknown[]) {
      (fbq as unknown as { queue?: unknown[][]; callMethod?: (...innerArgs: unknown[]) => void }).callMethod
        ? (fbq as unknown as { callMethod: (...innerArgs: unknown[]) => void }).callMethod(...args)
        : ((fbq as unknown as { queue: unknown[][] }).queue = [
            ...(((fbq as unknown as { queue?: unknown[][] }).queue ?? []) as unknown[][]),
            args,
          ]);
    } as unknown as ((...args: unknown[]) => void) & { queue?: unknown[][]; loaded?: boolean; version?: string };

    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    win.fbq = fbq;
    win._fbq = fbq;
  }

  const script = document.createElement("script");
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  win.fbq?.("init", pixelId);
  win.fbq?.("track", "PageView");
};

const applyConsent = (consent: CookieConsent | null) => {
  if (!consent) return;

  if (consent.analytics && gaMeasurementId) {
    loadGoogleAnalytics(gaMeasurementId);
  }

  if (consent.marketing && metaPixelId) {
    loadMetaPixel(metaPixelId);
  }
};

const ConsentScriptLoader = () => {
  useEffect(() => {
    applyConsent(getCookieConsent());

    const onConsentUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<CookieConsent>;
      applyConsent(customEvent.detail ?? getCookieConsent());
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, onConsentUpdated);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, onConsentUpdated);
  }, []);

  return null;
};

export default ConsentScriptLoader;
